// src/data/projects/entries/thesis.js
const project = {
  slug: "thesis",
  title: "Pimatgol, Walkable Again: Craft-Industry Regeneration in Jongno",
  category: "Student Project",
  date: "2022/09",
  excerpt: "Urban-regeneration strategy for Jongno’s historic Pimatgol that restores a human-scale, walkable alley and populates it with makers from the nearby Sewoon/Euljiro ecosystem—supported by a modular, parametric facade system that delivers shading and identity.",
  hero: "/images/projects/thesis/hero.png",
  heroSmall: "/images/projects/thesis/hero.png",
  heroThumb: "/images/projects/thesis/hero.thumb.png",
  thumb: "/images/projects/thesis/thumbnail.png",
  thumbPlaceholder: "/images/projects/thesis/thumbnail.thumb.png",
  content: [
    {
      type: "paragraph",
      text: `Tucked parallel to Seoul's bustling Jongno Avenue, Pimatgol alley has long been a hidden pedestrian passage with deep roots in the city's history. Originally created centuries ago to let commoners dodge noblemen on horseback along Jongno, this narrow lane grew into a lively corridor of shops, eateries, and everyday life. In recent decades, however, rapid redevelopment and neglect have left Pimatgol faded and largely forgotten—its once-vibrant alley culture at risk of disappearing.`,
    },
    {
      type: "image",
      src: "/images/projects/thesis/thesis_1.png",
      alt: "Pimatgol project image 1",
      placeholder: "/images/projects/thesis/thesis_1.thumb.png",
    },
    {
      type: "paragraph",
      text: `This graduation project reimagines Pimatgol as a pedestrian-first urban corridor that revives its spirit for the modern era. Rather than succumbing to large-scale redevelopment that wipes away the alley's character, the proposal takes a sensitive approach that preserves Pimatgol's human scale and social memory. The design introduces a network of small workshops, studios, and retail spaces run by local makers, breathing new life into the alley while celebrating the neighborhood's craft heritage.`,
    },
    {
      type: "image",
      src: "/images/projects/thesis/thesis_2.png",
      alt: "Pimatgol project image 2",
      placeholder: "/images/projects/thesis/thesis_2.thumb.png",
    },
    {
      type: "paragraph",
      text: `Key to the concept is the integration of the maker culture from the adjoining Sewoon and Euljiro districts—Seoul's historic manufacturing hub. Pimatgol's new workshops are operated by craftspeople, artists, and small manufacturers who utilize the neighborhood's existing industrial infrastructure. By turning the alley into an open-air extension of this maker community, the design not only revitalizes the street economy but also forges a symbiosis with the surrounding urban fabric. The once-forgotten backstreet is envisioned as a vibrant lane where pedestrians can observe, shop, and engage with craft and industry firsthand.`,
    },
    {
      type: "image",
      src: "/images/projects/thesis/thesis_3.png",
      alt: "Pimatgol project image 3",
      placeholder: "/images/projects/thesis/thesis_3.thumb.png",
    },
    {
      type: "paragraph",
      text: `In terms of urban design, the scheme transforms the physical layout of Pimatgol while respecting its intimate scale. The alley is pedestrianized and enhanced with better paving, lighting, and pocket plazas that invite people to linger. New infill structures line the route, carefully stepping up in height to remain context-sensitive. Ground floors are kept open and active—hosting maker workshops and tiny retail units that open directly onto the lane—ensuring continuous street life. Upper levels accommodate studios or living spaces for the makers, fostering a live-work community that animates the alley day and night.`,
    },
    {
      type: "image",
      src: "/images/projects/thesis/thesis_4.png",
      alt: "Pimatgol project image 4",
      placeholder: "/images/projects/thesis/thesis_4.thumb.png",
    },
    {
      type: "paragraph",
      text: `Architecturally, the project blends contemporary design with subtle references to craft. A unifying feature is the facade treatment: a modern curtain wall system wrapped by vertical twisted aluminum panels that act as both solar screens and decorative elements. These panels—three per floor—are parametrically designed, casting patterned shadows into the alley and evoking the texture of woven metal. This dynamic facade not only provides environmental control but also nods to the local metalworking and manufacturing heritage. The modular system was developed using computational design tools, allowing complex patterns to be optimized and fabricated with precision.`,
    },
    {
      type: "image",
      src: "/images/projects/thesis/thesis_5.png",
      alt: "Pimatgol project image 5",
      placeholder: "/images/projects/thesis/thesis_5.thumb.png",
    },
    {
      type: "paragraph",
      text: `Ultimately, the project strives to make Pimatgol walkable again in every sense. By revitalizing the alley's social and economic life and weaving new architecture into the historic fabric, it creates a vibrant public realm where old and new coexist. Pimatgol becomes a lively pedestrian passage once more—a catalyst for street life that serves as a model for alleyway regeneration in Seoul. This design proves that honoring local heritage and embracing innovation can go hand in hand, breathing new life into a treasured urban space.`,
    },
    {
      type: "image",
      src: "/images/projects/thesis/thesis_6.png",
      alt: "Pimatgol project image 6",
      placeholder: "/images/projects/thesis/thesis_6.thumb.png",
    },
  ],
};

export default project;
